/**
 * CONTROL: el cuadro de mandos del libro (3-oct-2026).
 *
 * Rais: «una sección nueva, Control… todas las estadísticas: cuántas palabras hay, cuál es el
 * estilo… el resumen estadístico: palabras, diferencia entre frases, alternancia de frases… y luego
 * la comparativa de datos de los manuscritos de la reescritura, cómo van cambiando estas cosas».
 *
 * Nada de aquí se inventa ni se pide de nuevo: el motor mide cada versión al escribirla y juzga los
 * diez ejes al hacer el informe de lectura. Esta pantalla lo junta. Si falta un dato, dice cómo
 * conseguirlo en vez de pintar una cifra de adorno.
 */
import { useEffect, useState } from "react";
import { bibliotecaEstilos, pasadas, ultimoInforme } from "../api";
import type { EjeEstilo, Informe, Maestro, Serie, Trabajo } from "../api";
import { MEDIDAS, cambioEnPalabras, cifra, comparativa, decisionesPorVersion, nombreDeRuta, valorDeMedida } from "../logica";
import { Radar } from "./Estilos";
import Pasadas from "./Pasadas";
import Gastos from "./Gastos";

export default function Control({ carpeta, doc, pulso = 0, trabajo, alLanzar, alCambiarDoc, alCambio, alIr, nombres }: {
  carpeta: string; doc: string | null; pulso?: number; trabajo?: Trabajo | null; alLanzar?: () => void;
  alCambiarDoc?: (ruta: string) => void; alCambio?: () => void; alIr?: (seccion: string) => void;
  nombres: Record<string, string>;
}) {
  const [serie, setSerie] = useState<Serie | null>(null);
  const [informe, setInforme] = useState<Informe | null>(null);
  const [ejes, setEjes] = useState<EjeEstilo[]>([]);
  const [maestros, setMaestros] = useState<Maestro[]>([]);

  useEffect(() => {
    if (!carpeta) return;
    pasadas(carpeta, doc).then(setSerie).catch(() => setSerie(null));
    ultimoInforme(carpeta, doc).then((i) => setInforme(i ?? null)).catch(() => setInforme(null));
  }, [carpeta, doc, pulso]);
  useEffect(() => {
    bibliotecaEstilos().then((r) => { setEjes(r.ejes); setMaestros(r.biblioteca); }).catch(() => {});
  }, []);

  if (!carpeta) {
    return <section className="carta"><h2>Control</h2><p className="vacio">Elige antes un proyecto: aquí se ven las cifras de tu libro.</p></section>;
  }

  const versiones = serie?.versiones ?? [];
  const ultima = [...versiones].sort((a, b) => a.n - b.n).at(-1) ?? null;
  const tabla = comparativa(versiones);
  const decisiones = decisionesPorVersion(versiones);
  const mios = informe?.data?.estilo_ejes ?? null;
  const hayEjes = !!mios && Object.values(mios).some((x) => x?.puntuacion != null);

  return (
    <>
      <div className="saludo"><h1>Control</h1><p>Las cifras de tu libro y cómo cambian de una versión a otra.</p></div>

      <section className="carta">
        <h2>Tu texto, ahora<span className="sub">{ultima ? (ultima.build ?? `versión ${ultima.n}`) : doc ? nombreDeRuta(doc) : "sin medir"}</span></h2>
        {ultima && MEDIDAS.some((m) => valorDeMedida(ultima, m.id) != null) ? (
          <div className="medidas">
            {MEDIDAS.map((m) => {
              const v = valorDeMedida(ultima, m.id);
              if (v == null) return null;
              return (
                <div className="medida" key={m.id}>
                  <span className="valor">{cifra(v, m.decimales)}{m.unidad === "%" ? " %" : ""}</span>
                  <b>{m.nombre}</b>
                  <span className="que">{m.que}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="vacio">Todavía no hay medidas de este libro. Se toman solas cada vez que el Studio escribe una versión; para medir
            las que ya tienes, pulsa «Medir la cadena» más abajo. No gasta nada.</p>
        )}
      </section>

      <section className="carta">
        {/* De qué versión es el dibujo: el último informe del libro puede ser de una anterior a la que
            tienes abierta (equipo rojo, 3-oct), y el Diagnóstico ya lo llama «de antes». */}
        <h2>Tu estilo<span className="sub">los diez ejes, según el informe de lectura{informe?.doc ? ` de ${informe.doc}` : ""}</span></h2>
        {informe?.doc && doc && nombreDeRuta(doc) !== informe.doc && (
          <p className="vacio">Este dibujo es de una versión anterior a la que tienes abierta ({nombreDeRuta(doc)}). Para verlo al día, rehaz el diagnóstico.</p>
        )}
        {hayEjes && ejes.length
          ? <Radar ejes={ejes} mios={mios!} maestros={maestros} />
          : <p className="vacio">El radar de tu estilo sale del diagnóstico. {alIr
              ? <button className="btn texto" onClick={() => alIr("diagnostico")}>Ir al Diagnóstico</button> : null}</p>}
      </section>

      {tabla.columnas.length >= 2 && tabla.filas.length > 0 && (
        <section className="carta">
          <h2>Cómo ha cambiado<span className="sub">de la versión {tabla.columnas[0].n} a la {tabla.columnas.at(-1)!.n}</span></h2>
          <div className="tabla-ancha">
            <table className="comparativa">
              <thead>
                <tr>
                  <th></th>
                  {tabla.columnas.map((c) => <th key={c.n} className="mono num">v{c.n}</th>)}
                  <th>Cambio</th>
                </tr>
              </thead>
              <tbody>
                {tabla.filas.map((f) => (
                  <tr key={f.medida.id} title={f.medida.que}>
                    <th scope="row">{f.medida.nombre}{f.medida.unidad === "%" ? " (%)" : ""}</th>
                    {f.valores.map((v, i) => <td key={i} className="mono num">{cifra(v, f.medida.decimales)}</td>)}
                    <td className="mono num cambio">{cambioEnPalabras(f.cambio, f.medida.decimales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="vacio">Cada versión se mide como quedaría con todos sus cambios aceptados. Pasa el ratón por una fila para ver qué mide.</p>
        </section>
      )}

      {/* M-9: lo que hiciste en Word con los cambios de cada versión. Solo con el motor que lo cuenta. */}
      {decisiones.columnas.length > 0 && decisiones.filas.length > 0 && (
        <section className="carta">
          <h2>Lo que has hecho con los cambios<span className="sub">leído de tus Word, versión a versión</span></h2>
          <div className="tabla-ancha">
            <table className="comparativa">
              <thead>
                <tr><th></th>{decisiones.columnas.map((c) => <th key={c.n} className="mono num">v{c.n}</th>)}</tr>
              </thead>
              <tbody>
                {decisiones.filas.map((f) => (
                  <tr key={f.id} title={f.que}>
                    <th scope="row">{f.nombre}</th>
                    {f.valores.map((v, i) => <td key={i} className="mono num">{cifra(v, 0)}</td>)}
                  </tr>
                ))}
                {decisiones.aceptacion.some((x) => x != null) && (
                  <tr title="De lo que ya has decidido, cuánto se quedó (aceptado, con o sin retoques)">
                    <th scope="row">Te quedaste con (%)</th>
                    {decisiones.aceptacion.map((x, i) => <td key={i} className="mono num cambio">{x == null ? "" : cifra(x, 0)}</td>)}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="vacio">Cada cambio cuenta una vez. Lo que sigue marcado en Word está sin decidir: decide allí (Revisar → Aceptar o Rechazar), guarda, y aquí se actualiza solo.</p>
        </section>
      )}

      <Pasadas carpeta={carpeta} doc={doc} pulso={pulso} trabajo={trabajo} alLanzar={alLanzar} alCambiarDoc={alCambiarDoc} alCambio={alCambio} />

      <details className="carta plegable">
        <summary><h2>Lo que llevas gastado<span className="sub">de tu llave de OpenRouter</span></h2></summary>
        <Gastos carpeta={carpeta} nombres={nombres} />
      </details>
    </>
  );
}
